/**
 * Preflight for attaching a parsed document to an existing ride.
 *
 * These mirror the server's own guards so the panel can explain the problem and
 * offer a fix, instead of letting the request fail with a code the manager
 * cannot act on. Run range first, then overlap — that is the server's order
 * (accommodation/views.py:76-82), and a card that fails both only ever reports
 * the range error.
 *
 * Dates are ISO 'YYYY-MM-DD' strings throughout, which compare correctly as
 * plain strings.
 */

const { fromBackendDate, toBackendDate, resolvePointDate } = require('./documentImportDates');

const BOOKED_STATUS = 'booked';
const MS_IN_DAY = 24 * 60 * 60 * 1000;

/**
 * Only booked stays collide. An ai_suggested_area or a base_trip_accomodation is
 * an offer, not a booking, and a real booking supersedes it in the ride view
 * (ride/views.py:464-472), so overlapping one is correct and must not warn.
 */
function bookedStays(rideAccommodations) {
  return (rideAccommodations || []).filter(a => a && a.status === BOOKED_STATUS);
}

/**
 * The inverse of the server's verefyNoDateCollision (views.py:433), which treats
 * touching dates as clear: one stay's check-out may equal the next check-in.
 */
function staysOverlap(aCheckIn, aCheckOut, bCheckIn, bCheckOut) {
  if (!aCheckIn || !aCheckOut || !bCheckIn || !bCheckOut) return false;
  const clear = (aCheckIn < bCheckIn && aCheckOut <= bCheckIn)
    || (aCheckIn >= bCheckOut && aCheckOut > bCheckOut);
  return !clear;
}

function findBookedConflict(checkIn, checkOut, rideAccommodations) {
  if (!checkIn || !checkOut) return null;
  const found = bookedStays(rideAccommodations).find(
    stay => staysOverlap(checkIn, checkOut, stay.check_in_date, stay.check_out_date),
  );
  return found || null;
}

function daysBetween(fromIso, toIso) {
  return Math.round((new Date(toIso) - new Date(fromIso)) / MS_IN_DAY);
}

/**
 * How far the ride would have to grow to contain this stay, or null when it
 * already does. Mirrors inRideDaytesRange (views.py:414): the ride's last day
 * counts, because the server stores end_date at 23:59:59.
 */
function rideRangeGap(rideStart, rideEnd, checkIn, checkOut) {
  if (!rideStart || !rideEnd || !checkIn || !checkOut) return null;
  if (checkIn >= checkOut) return null;
  if (rideStart <= checkIn && rideEnd >= checkOut) return null;

  const newStart = checkIn < rideStart ? checkIn : rideStart;
  const newEnd = checkOut > rideEnd ? checkOut : rideEnd;
  const daysAdded = daysBetween(newStart, rideStart) + daysBetween(rideEnd, newEnd);
  return { newStart, newEnd, daysAdded };
}

/**
 * The one-sided version of rideRangeGap's extension, for a single stray date.
 *
 * rideRangeGap's checkIn/checkOut pair is a stay: two equal dates mean zero
 * nights, which is invalid data and the function correctly refuses to act on
 * it (its guard at the top). A lone out-of-range point is not that -- one date
 * is entirely legitimate -- so this covers the case rideRangeGap deliberately
 * won't, using the same day-counting primitive it uses internally.
 */
function singleDayRangeGap(rideStart, rideEnd, date) {
  if (date < rideStart) {
    return { newStart: date, newEnd: rideEnd, daysAdded: daysBetween(date, rideStart) };
  }
  return { newStart: rideStart, newEnd: date, daysAdded: daysBetween(rideEnd, date) };
}

/**
 * How far the ride would have to grow to contain every out-of-range point at
 * once, or null when every point already fits (including when there are none
 * to check). One gap for the whole card, covering both directions, so a
 * single "extend the trip" click fixes it regardless of how many points or
 * which side they fall on.
 *
 * Each point's date is resolved with resolvePointDate -- the exact function
 * buildAttachOperations uses to set plannedDate -- so this preflight can never
 * flag (or clear) a point the actual attach would treat differently. Its
 * fallback to rideStart for a point with neither a date nor a day number is
 * inherited here too: such a point is always in range, correctly.
 */
function pointsRangeGap(rideStart, rideEnd, points) {
  if (!rideStart || !rideEnd) return null;
  const list = (points || []).filter(Boolean);
  if (list.length === 0) return null;

  const outOfRangeDates = list
    .map(point => resolvePointDate(point, rideStart))
    .filter(date => date && (date < rideStart || date > rideEnd));

  if (outOfRangeDates.length === 0) return null;

  const earliest = outOfRangeDates.reduce((min, d) => (d < min ? d : min));
  const latest = outOfRangeDates.reduce((max, d) => (d > max ? d : max));

  // Compose with rideRangeGap for the general case: treating the earliest and
  // latest out-of-range dates as a pseudo check-in/check-out pair, its own
  // extension maths already grows the range to cover both ends at once. Only
  // when every out-of-range point shares that one date does its guard reject
  // the (equal) pair, so that single case falls to the helper above instead.
  const gap = earliest === latest
    ? singleDayRangeGap(rideStart, rideEnd, earliest)
    : rideRangeGap(rideStart, rideEnd, earliest, latest);

  return Object.assign({}, gap, { outOfRangeCount: outOfRangeDates.length });
}

/**
 * The ride's stays as one flat list with ISO dates.
 *
 * rideAccommodationApi (accommodation/views.py:543) answers an OBJECT, not an
 * array -- { ride: [...], baseRide: [...] } -- and drops any key whose list is
 * empty, so it can arrive as {}, as one key, or as both. Handing that object
 * straight to findBookedConflict throws, so everything goes through here.
 *
 * The dates are rewritten on the way: the serializer emits DD-MM-YYYY
 * (common/convertToDictionary.py:18-19) while every comparison in this file is a
 * plain string compare that only works on ISO YYYY-MM-DD.
 */
function flattenRideStays(allAccommodations) {
  const source = allAccommodations || {};
  return [...(source.ride || []), ...(source.baseRide || [])]
    .filter(Boolean)
    .map(stay => Object.assign({}, stay, {
      check_in_date: fromBackendDate(stay.check_in_date),
      check_out_date: fromBackendDate(stay.check_out_date),
    }));
}

/**
 * Whether allAccommodations is actually shaped like rideAccommodationApi's
 * contract -- { ride?: [...], baseRide?: [...] } -- rather than something
 * flattenRideStays can only read as "no stays" by accident.
 *
 * That accident is not hypothetical: check_gui_version (gui_version.py:14-22)
 * answers a version-error OBJECT, { ServerMassageCode: 'gui_version_outdated' },
 * IN PLACE OF the stay list on this exact endpoint whenever the ride has
 * vouchers (accommodation/api.py:20-28) -- which is every ride this panel's own
 * feature creates. Neither key of that object is an array, so it would flatten
 * to [] and the double-booking preflight would report a clean trip that never
 * got checked. This predicate exists so the caller can tell the two apart and
 * refuse instead of guessing.
 *
 * A genuinely empty trip answers {} (the server drops each key whose list is
 * empty -- views.py:555) or omits one bucket entirely, and both count as
 * readable: there is a difference between "asked and got nothing" and "could
 * not ask".
 */
function staysAreReadable(allAccommodations) {
  if (allAccommodations === null || allAccommodations === undefined) return false;
  if (typeof allAccommodations !== 'object' || Array.isArray(allAccommodations)) return false;
  if (Object.prototype.hasOwnProperty.call(allAccommodations, 'ServerMassageCode')) return false;
  const { ride, baseRide } = allAccommodations;
  if (ride !== undefined && !Array.isArray(ride)) return false;
  if (baseRide !== undefined && !Array.isArray(baseRide)) return false;
  return true;
}

/**
 * Whether two ride ids refer to the same ride. Exists because
 * rootState.ride.lowedRideId is set from the route param on the ride page
 * (SingleRideWithMap.vue's created()) -- a STRING -- while a caller's own id
 * is frequently a Number (RideDocumentsPanel's `rideId` prop, sourced from
 * the ride header's own numeric id). Comparing those with === would never
 * match, which would leave a fetched-and-ready stay list permanently
 * mistaken for a stale one. null and undefined never match anything,
 * including each other or themselves.
 */
function sameRideId(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
}

/**
 * Whether a stay fetch already in the store covers `rideId`, so a caller
 * about to fetch can skip a redundant request: the store's own id matches
 * AND its status is 'ready' (already landed) or 'loading' (already in
 * flight). Every other combination -- a stale id left over from a different
 * ride, 'idle' (never fetched), or 'error' (a previous fetch failed) -- means
 * a fetch is still needed.
 */
function accommodationsFetchCovers(rideId, accommodationsRideId, accommodationsStatus) {
  return sameRideId(rideId, accommodationsRideId)
    && (accommodationsStatus === 'ready' || accommodationsStatus === 'loading');
}

/**
 * Whether allAccommodations, as tracked by accommodationsRideId /
 * accommodationsStatus, can actually be trusted as an answer for `rideId`'s
 * double-booking check right now: fetched for the right ride, successfully,
 * AND shaped like a real answer (staysAreReadable). A never-fetched ride and
 * a genuinely stay-free one are byte-identical ({}), so the raw value alone
 * can never tell them apart -- accommodationsRideId and accommodationsStatus
 * are what make the difference visible. Any state other than "matches, and
 * ready, and readable" -- never fetched, mid-fetch, a stale id from a
 * different ride, or a failed fetch -- must not be read as "no conflicts".
 */
function staysCheckReady({
  rideId, accommodationsRideId, accommodationsStatus, allAccommodations,
}) {
  return sameRideId(rideId, accommodationsRideId)
    && accommodationsStatus === 'ready'
    && staysAreReadable(allAccommodations);
}

/** Whether a fetch for `rideId`'s stays is currently in flight. */
function staysCheckLoading(rideId, accommodationsRideId, accommodationsStatus) {
  return sameRideId(rideId, accommodationsRideId) && accommodationsStatus === 'loading';
}

/**
 * The one thing standing between this document and the ride, or null.
 *
 * Order matters and is the server's own (accommodation/views.py:76-82): range
 * first, overlap second. A stay that fails both is only ever told about the
 * range, so reporting the overlap first would have the manager move an existing
 * booking and then meet a date error on the same card.
 *
 * 'invalidDates' comes before either because rideRangeGap answers null for a
 * reversed or zero-night stay (it cannot extend a ride to fit a range that does
 * not exist), so without this check a malformed voucher passes preflight and
 * fails server-side in a popup with nothing but a close button.
 */
function accommodationBlocker({
  checkIn, checkOut, rideStart, rideEnd, stays,
}) {
  if (!checkIn || !checkOut) return null;
  if (checkIn >= checkOut) return { kind: 'invalidDates' };

  const gap = rideRangeGap(rideStart, rideEnd, checkIn, checkOut);
  if (gap) return { kind: 'range', gap };

  const conflict = findBookedConflict(checkIn, checkOut, stays);
  if (conflict) return { kind: 'doubleBooking', conflict };

  return null;
}

/**
 * The body of an update_accommodation_api call that moves a stay's dates.
 *
 * That endpoint is a FULL REPLACE, not a patch (accommodation/views.py:297-314
 * assigns name, address, link, text, booking_confirmation and image straight
 * from the form), and it reads GUI camelCase while the serializer answers
 * snake_case. Spreading the serialized row into it would blank the manager's
 * address, link, notes and confirmation number on a real booking, so the mapping
 * is written out field by field, matching getupdatedFormaccommodation
 * (store/modules/accommodation.js:461-480).
 *
 * `rate` is deliberately absent: the serializer never returns it, and the server
 * guards the assignment with `if accommodationForm.get('rate')` (views.py:316),
 * so leaving it out keeps the existing rating while sending null or 0 would be
 * the same no-op today and a wipe the day that guard becomes `in`.
 *
 * `bookingConfirmation` is only sent when the row actually carried one: the
 * serializer omits it for a viewer who is not the ride owner
 * (convertToDictionary.py:27-28) and an absent key must not be turned into ''.
 */
function buildStayDateUpdatePayload(stay, checkInIso, checkOutIso) {
  const payload = {
    name: stay.name || '',
    address: stay.address || '',
    text: stay.text || '',
    link: stay.link || null,
    checkIn: toBackendDate(checkInIso),
    checkOut: toBackendDate(checkOutIso),
    lat: stay.latitude,
    lng: stay.longtitude,
    contryAndCity: stay.cuntry_and_city,
    image: stay.image,
    place_id: stay.place_id,
    voucherUrls: Array.isArray(stay.voucherUrls) ? stay.voucherUrls : [],
  };
  if (stay.booking_confirmation !== undefined && stay.booking_confirmation !== null) {
    payload.bookingConfirmation = stay.booking_confirmation;
  }
  return payload;
}

/**
 * A translation key for every ServerMassageCode the repair actions can meet.
 *
 * This exists because showModalWithParameters renders a code only when it is a
 * key of errorNumberConverter and drops anything else without a word
 * (store/modules/popups.js:716). Two of the codes below are not keys there --
 * "fail - date are not at ride range", which updateaccommodationApi answers
 * (accommodation/views.py:291) and which is a DIFFERENT string from the create
 * path's "date_are_not_in_range", and "general_error" -- so routing them to a
 * popup is a button press that does nothing at all.
 *
 * Both functions answer a key for every input, known or not: silence is the one
 * outcome that is never acceptable here.
 */
const STAY_UPDATE_ERROR_KEYS = {
  // accommodation/views.py:291 -- note the spelling, it is not the create code.
  'fail - date are not at ride range': 'rideDocumentsExistingOutOfRange',
  // accommodation/views.py:293
  you_Are_having_double_booking: 'rideDocumentsExistingStillCollides',
  // accommodation/api.py:37
  user_is_not_authorized: 'rideDocumentsExistingNotAllowed',
  // accommodation/views.py:288
  master_accommodation_already_exists: 'rideDocumentsExistingSaveFailed',
  // accommodation/api.py:31 -- the route's own default, e.g. a non-POST.
  fail_to_update_accommodation: 'rideDocumentsExistingSaveFailed',
};

function stayUpdateErrorKey(code) {
  return STAY_UPDATE_ERROR_KEYS[code] || 'rideDocumentsExistingSaveFailed';
}

/** deleteAccommodationApi answers only these (accommodation/api.py:16-25). */
function stayDeleteErrorKey(code) {
  if (code === 'user_is_not_authorized') return 'rideDocumentsExistingNotAllowed';
  return 'rideDocumentsExistingRemoveFailed';
}

module.exports = {
  BOOKED_STATUS,
  STAY_UPDATE_ERROR_KEYS,
  stayUpdateErrorKey,
  stayDeleteErrorKey,
  bookedStays,
  staysOverlap,
  findBookedConflict,
  rideRangeGap,
  pointsRangeGap,
  flattenRideStays,
  staysAreReadable,
  sameRideId,
  accommodationsFetchCovers,
  staysCheckReady,
  staysCheckLoading,
  accommodationBlocker,
  buildStayDateUpdatePayload,
};
